import { captureError, SENTRY_SEVERITY, toTimerError } from 'src/shared/utils/sentry'

type TimerPhase = 'start' | 'reset'

/** 시작 2초 뒤 감소/재전송 검사 간격(ms) */
const TICK_CHECK_MS = 2000

/**
 * 타이머 모니터링 실패를 Sentry TimerError로 전송한다.
 * fingerprint: ['timer', phase]
 */
export function captureTimerError(
  phase: TimerPhase,
  message: string,
  extra?: Record<string, unknown>,
) {
  captureError(toTimerError(new Error(`[timer] ${message}`)), {
    level: SENTRY_SEVERITY.Warning,
    tags: { phase, path: window.location.pathname },
    fingerprint: ['timer', phase],
    extra,
  })
}

/**
 * 시작 2초 뒤 감소/재전송 검사를 예약한다.
 * 이미 다른 타이머로 바뀌었거나(재시작·stop), 탭이 백그라운드(document.hidden)면 이슈로 보내지 않는다.
 */
export function scheduleTimerChecks(params: {
  isCurrent: () => boolean // 현재 타이머 여부 (재전송, stop 의 경우 false로 반환)
  getRemainingSec: () => number // 현재 타이머 남은 초
  isResend: boolean // 재전송 여부
  previousRemainingSec: number // begin 직전 남은 초
  timeout: number // 서버 TimeOut (초).
}) {
  const { isCurrent, getRemainingSec, isResend, previousRemainingSec, timeout } = params

  window.setTimeout(() => {
    // 이전 타이머용 예약이거나 탭이 백그라운드면 스킵
    if (!isCurrent() || document.hidden) return

    const remainingSec = getRemainingSec()

    if (remainingSec <= 0 || remainingSec >= timeout) {
      captureTimerError('start', '타이머가 감소하지 않음', {
        timeout,
        remainingSec,
        previousRemainingSec,
      })
      return
    }
    if (isResend && previousRemainingSec <= timeout - 3 && remainingSec <= previousRemainingSec) {
      captureTimerError('reset', '재전송 후 타이머가 초기화되지 않음', {
        timeout,
        remainingSec,
        previousRemainingSec,
      })
      return
    }
  }, TICK_CHECK_MS)
}

export function createTimerMonitor() {
  let sessionId = 0
  let remainingSec = 0

  return {
    syncRemainingSec(seconds: number) {
      remainingSec = seconds
    },
    /** stop / 페이지 이탈 시. 이전 예약 검사 무효화 (remainingSec 유지) */
    invalidate() {
      sessionId += 1
    },
    begin(timeout: number): boolean {
      const previousRemainingSec = remainingSec
      const isResend = previousRemainingSec > 0
      const thisSessionId = ++sessionId

      if (!Number.isFinite(timeout) || timeout <= 0) {
        captureTimerError('start', '서버 TimeOut 무효', { timeout })
        return false
      }

      scheduleTimerChecks({
        isCurrent: () => thisSessionId === sessionId,
        getRemainingSec: () => remainingSec,
        isResend,
        previousRemainingSec,
        timeout,
      })
      return true
    },
  }
}
